import styled from '@emotion/styled';
import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import Toggle from '~ComponentsImproved/Toggle';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import Adyen from '~Scenes/Settings-v2/Scenes/Adyen';
import TerminalSetup from '~Scenes/Handy/Scenes/Settings/Payments/TerminalSetup';
import { HIDE_IN, TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';
import { BlueButton, Box } from '~Styles';
import LoomisSetup from './LoomisSetup';

const AdyenWrapper = styled.div`
  margin-top: 48px;

  & > div {
    padding: 0;
  }
`;

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const PaymentsSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const logger = useLogger('settings-v2');
  const [storage, commitToStorage, storageLoading] = useStorage();
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const hasCash = paymentMethods.find((method) => method.id === 'CASH')?.active;
  const hasZettle = paymentMethods.find((method) => method.id === 'IZETTLE')?.active;
  const hasAdyen = paymentMethods.find((method) => method.id === 'ADYEN')?.active;
  const hasInvoice = paymentMethods.find((method) => method.id === 'INVOICE')?.active;
  const hasLoomis = paymentMethods.find((method) => method.id === 'LOOMIS')?.active;

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Payments Settings)', { fields: data });
    commitToStorage(data);
  }

  const zettleButton = useButton({
    onClick: () => {
      logger.info('Opening iZettle settings');
      window.postMessageBridge({ showIZettleSettings: true });
    },
  });

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.EnableTipping.Text'),
      key: 'enableTipping',
      storage: storage.enableTipping,
    },
    {
      label: i18n('Disable.Cash.Text'),
      key: 'disableCash',
      storage: storage.disableCash,
      hideIn: !hasCash ? HIDE_IN.BOTH : undefined,
    },
    {
      label: i18n('Handy.DirectPayment'),
      subLabel: i18n('Handy.DirectPayment.Text'),
      key: 'handyDirectPayment',
      storage: storage.handyDirectPayment,
      hideIn: HIDE_IN.TABLET,
    },
    {
      label: i18n('Settings.EnableInvoiceCustomerCreation.Text'),
      key: 'enableInvoiceCustomerCreation',
      storage: storage.enableInvoiceCustomerCreation,
      hideIn: !hasInvoice ? HIDE_IN.BOTH : undefined,
    },
  ];

  if (storageLoading) {
    return null;
  }

  /** Renders a toggle row from a toggle config entry. */
  const renderToggle = (toggle: TogglesConfig) =>
    !isHiddenOnDevice(toggle.hideIn, isHandy) && (
      <Toggle
        key={toggle.key}
        settingKey={toggle.key}
        label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
        description={toggle.subLabel}
        initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
        onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
        fullWidth
        darkBackground
        lightText
        compact={isHandy}
      />
    );

  return (
    <Box padding={isHandy ? 'none' : 'large'} marginTop={isHandy ? 'small' : 'none'}>
      <SettingsBlock compact={isHandy}>{togglesConfig.map(renderToggle)}</SettingsBlock>

      {/* A Loomis terminal is set by its IP. Otherwise handheld registers its terminals
          through the app bridge, tablets configure Adyen in-app */}
      {hasLoomis ? (
        <LoomisSetup compact={isHandy} />
      ) : isHandy ? (
        <TerminalSetup />
      ) : (
        <>
          {hasZettle && (
            <Box marginTop="large">
              <BlueButton {...zettleButton}>{i18n('Settings.Payments.OpenZettle')}</BlueButton>
            </Box>
          )}

          {hasAdyen && (
            <AdyenWrapper>
              <Adyen />
            </AdyenWrapper>
          )}
        </>
      )}
    </Box>
  );
};

export default PaymentsSettings;
